import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  COOKIE_CONSENT_EVENT,
  getEffectiveCookieConsent,
  getStoredCookieConsent,
  persistCookieConsent,
  canSyncCookieConsent,
  syncCookieConsentToServer,
} from '../../utils/cookieConsent';
import { isPrerendering } from '../../utils/prerendering';
import { AuthContext } from '../../contexts/AuthContext';
import { useUpdateAppSettings } from '../../hooks/useBackend';

const overlayStyle = {
  position: 'fixed',
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 2000,
  width: '100%',
  maxWidth: '100vw',
  boxSizing: 'border-box',
  padding: '12px 14px',
  background: 'rgba(15, 19, 32, 0.92)',
  borderTop: '1px solid rgba(255,255,255,0.12)',
  backdropFilter: 'blur(12px)',
};

const containerStyle = {
  width: '100%',
  maxWidth: '1100px',
  margin: '0 auto',
  minWidth: 0,
  boxSizing: 'border-box',
  display: 'flex',
  gap: '12px',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'nowrap',
};

const buttonBase = {
  borderRadius: 4,
  padding: '8px 12px',
  fontWeight: 600,
  border: '1px solid rgba(255,255,255,0.18)',
  cursor: 'pointer',
  fontSize: '0.9rem',
  whiteSpace: 'nowrap',
  flexShrink: 0,
};

const consentKey = (value) => (value ? JSON.stringify(value) : null);

const CookieConsentBanner = () => {
  const { user, updateUserSettings, isInitializing } = useContext(AuthContext);
  const { updateAppSettings } = useUpdateAppSettings();
  const updateUserSettingsRef = useRef(updateUserSettings);
  const updateAppSettingsRef = useRef(updateAppSettings);
  const syncAttemptedRef = useRef(null);
  const [consent, setConsent] = useState(() => getStoredCookieConsent());

  updateUserSettingsRef.current = updateUserSettings;
  updateAppSettingsRef.current = updateAppSettings;

  const remoteConsentKey = user?.appSettings?.cookieConsent
    ? JSON.stringify(user.appSettings.cookieConsent)
    : null;

  const applyResolvedConsent = (appSettings) => {
    const resolved = getEffectiveCookieConsent(appSettings);
    setConsent((prev) => (consentKey(prev) === consentKey(resolved) ? prev : resolved));
    return resolved;
  };

  // Hydrate from account settings when server consent changes.
  useEffect(() => {
    applyResolvedConsent(user?.appSettings);
  }, [remoteConsentKey]);

  // Push local consent to server once after login when account has none yet.
  // Wait until settings have loaded. Email login sets the token first and
  // fetches settings after; syncing in that gap used to replace app_settings
  // with { cookieConsent } and drop the starred account.
  useEffect(() => {
    const token = user?.token;
    const appSettings = user?.appSettings;
    if (!canSyncCookieConsent({ token, isInitializing, appSettings, remoteConsentKey })) {
      if (!token || remoteConsentKey) syncAttemptedRef.current = null;
      return;
    }

    const local = getStoredCookieConsent();
    if (!local) return;

    const syncKey = `${token}:${consentKey(local)}`;
    if (syncAttemptedRef.current === syncKey) return;
    syncAttemptedRef.current = syncKey;

    let cancelled = false;

    (async () => {
      await syncCookieConsentToServer({
        token,
        appSettings,
        updateAppSettings: (...args) => updateAppSettingsRef.current(...args),
        updateUserSettingsInAuth: (...args) => updateUserSettingsRef.current(...args),
      });
      if (!cancelled) {
        applyResolvedConsent(appSettings);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.token, user?.appSettings, isInitializing, remoteConsentKey]);

  // Keep banner in sync if consent is written elsewhere (another tab or event).
  useEffect(() => {
    const refreshConsent = () => {
      applyResolvedConsent(user?.appSettings);
    };

    const onStorage = (e) => {
      if (!e) return;
      if (e.key && !String(e.key).startsWith('pt_cookie_consent_v')) return;
      refreshConsent();
    };

    window.addEventListener('storage', onStorage);
    window.addEventListener(COOKIE_CONSENT_EVENT, refreshConsent);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(COOKIE_CONSENT_EVENT, refreshConsent);
    };
  }, [remoteConsentKey]);

  const waitingForAccountSettings = !!localStorage.getItem('authToken') && isInitializing;
  const shouldShow = useMemo(
    () => !consent && !isPrerendering() && !waitingForAccountSettings,
    [consent, waitingForAccountSettings],
  );

  if (!shouldShow) return null;

  const saveConsent = async (choices) => {
    const next = await persistCookieConsent(choices, {
      token: user?.token,
      appSettings: user?.appSettings,
      updateAppSettings,
      updateUserSettingsInAuth: updateUserSettings,
    });
    setConsent(next);
  };

  const acceptAll = () => saveConsent({ analytics: true, marketing: true });

  const rejectNonEssential = () => {
    saveConsent({ analytics: false, marketing: false });
    // Note: We do NOT call posthog.opt_out_capturing() - pageviews and interactions
    // are always tracked for product analytics regardless of cookie consent.
  };

  return (
    <div style={overlayStyle} role="region" aria-label="Cookie consent">
      <div style={containerStyle}>
        <div style={{ flex: '1 1 0%', minWidth: 0 }}>
          <div style={{ color: '#fff', fontWeight: 700, marginBottom: 2, fontSize: '0.9rem' }}>
            Cookies & privacy
          </div>
          <div style={{ color: 'rgba(255,255,255,0.88)', lineHeight: 1.35, fontSize: '0.875rem' }}>
            Essential cookies stay on. Analytics and marketing are optional.{' '}
            <Link to="/privacy" style={{ color: '#9AA6FF', textDecoration: 'underline' }}>
              Privacy
            </Link>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flex: '0 0 auto', flexWrap: 'nowrap' }}>
          <button
            type="button"
            onClick={rejectNonEssential}
            style={{ ...buttonBase, background: 'rgba(255,255,255,0.08)', color: '#fff', borderRadius: 4 }}
          >
            Reject
          </button>
          <button
            type="button"
            onClick={acceptAll}
            style={{
              ...buttonBase,
              background: '#5B6CFF',
              borderColor: '#5B6CFF',
              color: '#E8EDF4',
              borderRadius: 4,
            }}
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsentBanner;
